import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatINR } from "@/api/client";
import { Package, MapPin, Heart, User, Truck, Star, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BuyerDashboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);

  const loadReviews = () => {
    api.get("/my/reviews").then((r) => setMyReviews(r.data));
    api.get("/my/pending-reviews").then((r) => setPendingReviews(r.data));
  };

  useEffect(() => {
    if (!user) return;
    api.get("/orders/me").then((r) => setOrders(r.data));
    api.get("/wishlist").then((r) => setWishlist(r.data));
    api.get("/addresses").then((r) => setAddresses(r.data));
    loadReviews();
  }, [user]);

  const deleteReview = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/reviews/${r.id}`); toast.success("Deleted"); loadReviews(); }
    catch { toast.error("Delete failed"); }
  };

  if (!user) {
    return <div className="container-fah py-20 text-center"><h2 className="text-xl">Please <Link to="/login" className="text-primary underline">login</Link></h2></div>;
  }

  return (
    <div className="container-fah py-8 sm:py-12" data-testid="page-buyer-dashboard">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold">Hi, {user.name?.split(" ")[0] || "Farmer"} 👋</h1>
        <p className="text-muted-foreground text-sm">Manage your orders, wishlist & addresses</p>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        <aside className="card-fah p-2 h-fit">
          {[
            { k: "orders", l: "My Orders", I: Package, c: orders.length },
            { k: "reviews", l: "My Reviews", I: Star, c: myReviews.length },
            { k: "wishlist", l: "Wishlist", I: Heart, c: wishlist.length },
            { k: "addresses", l: "Addresses", I: MapPin, c: addresses.length },
            { k: "profile", l: "Profile", I: User },
          ].map(({ k, l, I, c }) => (
            <button key={k} onClick={() => setTab(k)} className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm ${tab === k ? "bg-primary text-white" : "hover:bg-muted"}`} data-testid={`dash-tab-${k}`}>
              <span className="flex items-center gap-2"><I className="w-4 h-4" /> {l}</span>
              {c !== undefined && <span className={`text-xs ${tab === k ? "bg-white/20" : "bg-muted"} px-1.5 rounded`}>{c}</span>}
            </button>
          ))}
        </aside>

        <main>
          {tab === "orders" && (
            <div className="space-y-3" data-testid="orders-list">
              {orders.length === 0 && <div className="card-fah p-8 text-center text-muted-foreground">No orders yet. <Link to="/products" className="text-primary underline">Start shopping</Link></div>}
              {orders.map((o) => (
                <Link key={o.id} to={`/order/${o.id}`} className="card-fah p-4 block hover-lift" data-testid={`order-${o.order_number}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Order #{o.order_number}</div>
                      <div className="font-semibold mt-0.5">{o.items.length} items · {formatINR(o.total)}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Placed on {new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full uppercase ${
                      o.status === "delivered" ? "bg-green-100 text-green-700"
                      : o.status === "cancelled" || o.status === "returned" ? "bg-red-100 text-red-700"
                      : o.status === "shipped" || o.status === "out_for_delivery" ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700"
                    }`}>{o.status.replace(/_/g, " ")}</span>
                  </div>
                  <div className="mt-3 flex gap-2 overflow-x-auto">
                    {o.items.slice(0, 4).map((it, i) => (
                      <div key={i} className="flex items-center gap-2 shrink-0 bg-muted/40 rounded-lg p-2">
                        <img src={it.image} alt={it.name} className="w-10 h-10 rounded object-cover" />
                        <div className="text-xs">
                          <div className="font-medium line-clamp-1 max-w-[140px]">{it.name}</div>
                          <div className="text-muted-foreground">Qty: {it.qty}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground border-t border-border pt-3">
                    <div className="flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5" />
                      {o.tracking_id ? <span>Tracking: <span className="font-mono font-semibold">{o.tracking_id}</span></span>
                        : o.status === "placed" ? "Your order has been placed"
                        : o.status === "shipped" ? "Your order is on the way"
                        : o.status === "delivered" ? "Delivered successfully"
                        : `Status: ${o.status.replace(/_/g, " ")}`}
                    </div>
                    <span className="text-primary font-semibold">View details →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {tab === "reviews" && (
            <div className="space-y-4" data-testid="my-reviews">
              {pendingReviews.length > 0 && (
                <div className="card-fah p-4">
                  <div className="text-sm font-display font-bold mb-2">Delivered products waiting for your review ({pendingReviews.length})</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {pendingReviews.map((p) => (
                      <Link key={p.product_id + p.order_id} to={`/product/${p.product_id}`} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted transition" data-testid={`pending-review-${p.product_id}`}>
                        <img src={p.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium line-clamp-1">{p.product_name}</div>
                          <div className="text-[10px] text-muted-foreground">Delivered · Tap to review</div>
                        </div>
                        <Star className="w-4 h-4 text-secondary" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {myReviews.length === 0 && pendingReviews.length === 0 && (
                <div className="card-fah p-8 text-center text-muted-foreground">No reviews yet. After a product is delivered, you'll be able to review it here.</div>
              )}
              {myReviews.map((r) => (
                <div key={r.id} className="card-fah p-4" data-testid={`my-review-${r.id}`}>
                  <div className="flex items-start gap-3">
                    {r.product_image && <img src={r.product_image} alt="" className="w-14 h-14 rounded object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${r.product_id}`} className="text-sm font-semibold hover:text-primary line-clamp-1">{r.product_name}</Link>
                      <div className="flex items-center gap-1 mt-1">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />)}</div>
                      {r.title && <div className="font-semibold text-sm mt-1">{r.title}</div>}
                      <p className="text-sm text-foreground/80 mt-1 line-clamp-3">{r.comment}</p>
                      {r.images?.length > 0 && (
                        <div className="mt-2 flex gap-1.5 flex-wrap">
                          {r.images.slice(0, 5).map((img, i) => <img key={i} src={img} alt="" className="w-10 h-10 rounded object-cover" />)}
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground mt-1.5">{new Date(r.created_at).toLocaleDateString("en-IN")} · {r.helpful_count || 0} found helpful</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Link to={`/product/${r.product_id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Edit"><Edit className="w-4 h-4" /></Link>
                      <button onClick={() => deleteReview(r)} className="p-1.5 rounded hover:bg-muted text-destructive" title="Delete" data-testid={`del-my-review-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {r.admin_reply && (
                    <div className="mt-3 pl-3 border-l-2 border-primary bg-primary/5 rounded-r p-2 text-xs">
                      <b>Reply from FarmersAgroHub:</b> {r.admin_reply}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="wishlist-list">
              {wishlist.length === 0 ? <div className="card-fah p-8 text-center text-muted-foreground">Your wishlist is empty</div> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wishlist.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} className="card-fah p-3 hover-lift flex gap-3">
                      <img src={p.images[0]} alt={p.name} className="w-20 h-20 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold line-clamp-2">{p.name}</div>
                        <div className="font-bold mt-1">{formatINR(p.price)}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "addresses" && (
            <AddressManager addresses={addresses} setAddresses={setAddresses} />
          )}

          {tab === "profile" && (
            <div className="card-fah p-6" data-testid="profile-info">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Name" value={user.name} />
                <Info label="Email" value={user.email} />
                <Info label="Phone" value={user.phone || "Not set"} />
                <Info label="Role" value={user.role} />
                <Info label="Member since" value={new Date(user.created_at).toLocaleDateString("en-IN")} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div><div className="font-semibold mt-0.5 capitalize">{value}</div></div>
);

const AddressManager = ({ addresses, setAddresses }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: false });
  const save = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/addresses", form);
    setAddresses([...addresses, data]); setOpen(false);
    setForm({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: false });
  };
  const del = async (id) => { await api.delete(`/addresses/${id}`); setAddresses(addresses.filter((a) => a.id !== id)); };

  return (
    <div data-testid="address-list">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-muted-foreground">{addresses.length} saved address(es)</div>
        <button onClick={() => setOpen(!open)} className="bg-primary text-white text-sm font-semibold py-2 px-4 rounded-lg" data-testid="btn-add-address">{open ? "Cancel" : "+ Add Address"}</button>
      </div>
      {open && (
        <form onSubmit={save} className="card-fah p-4 grid sm:grid-cols-2 gap-3 mb-4">
          {["full_name", "phone", "line1", "city", "state", "pincode"].map((k) => (
            <input key={k} placeholder={k.replace("_", " ").toUpperCase()} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required className="px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid={`addr-input-${k}`} />
          ))}
          <button type="submit" className="bg-secondary text-secondary-foreground font-semibold py-2 px-4 rounded-lg sm:col-span-2" data-testid="btn-save-address">Save Address</button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {addresses.map((a) => (
          <div key={a.id} className="card-fah p-4">
            <div className="font-semibold">{a.full_name} <span className="font-normal text-muted-foreground">· {a.phone}</span></div>
            <div className="text-sm text-muted-foreground mt-1">{a.line1}, {a.city}, {a.state} - {a.pincode}</div>
            <button onClick={() => del(a.id)} className="mt-2 text-xs text-destructive hover:underline">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BuyerDashboard;
